import _ from "lodash";
import Backbone from "backbone";
import Promise from "bluebird";
import inject from "scripts/ioc/inject";
import BaseMediaDetailsPageView from "scripts/views/baseMediaDetailsPageView";
import ModulePageNavView from "scripts/views/modulePageNavView";
import PagerView from "scripts/views/ellipsisPagerView";
import MediaListView from "scripts/views/mediaListView";
import MediaSalvattoreGridView from "scripts/views/mediaSalvattoreGridView";
import {
  redirectToFragment,
  replaceFragment,
} from "scripts/utils/routerHelpers";

import templateModulePage from "templates/modulePage.hbs";
import FacetedSearchResultCollection from "../collections/facetedSearchResultCollection";

const chan = Backbone.Radio.channel;

const PAGE_SIZE = 20;

// const USE_SEARCH = [
//     "52f18a35-4b40-4b56-8c6f-d0e79cad6167", "springer",
//     "9c9adc5a-b39d-431f-8018-93bc07190700", "springer-protocols",
//     "747ff7e1-5e93-4d05-a55c-9ca7b8cc795e", "wiley"
// ]

class ModulePageView extends BaseMediaDetailsPageView {
  constructor(
    options,
    breadcrumbService = inject("breadcrumbService"),
    connectionService = inject("connectionService"),
    securityService = inject("securityService"),
  ) {
    super(options, breadcrumbService, connectionService, securityService);

    this.model.on(
      "change:presentationType",
      this.onPresentationTypeChangeSaveUserPreference,
      this,
    );

    const pageIndex = this.model.get("pageIndex");

    this.collection = new FacetedSearchResultCollection([], {
      path: "/search/v2",
      state: {
        currentPage: pageIndex,
        pageSize: PAGE_SIZE,
      },
    });

    /**
     * Hack to satisfy Backbone.PageableCollection._checkState
     * if starting from a page other than 0.
     */
    if (pageIndex > 0) {
      this.collection.links[pageIndex] = this.collection.url;
    }
  }

  get events() {
    return _.extend({}, super.events, {
      "click #bb-secondary-sticky-nav-presentation-type-toggle-list":
        "onClickListView",
      "click #bb-secondary-sticky-nav-presentation-type-toggle-masonry":
        "onClickMasonryView",
    });
  }

  get template() {
    return templateModulePage;
  }

  isOverflowed(element) {
    return (
      element &&
      (element.scrollHeight > element.clientHeight ||
        element.scrollWidth > element.clientWidth)
    );
  }

  render() {
    super.render();

    this.renderCurationButton();
    this.renderPageHeaderDescriptionContainer();

    const $description = $("#description-container");
    if (!this.isOverflowed($description[0])) {
      const $descriptionContainer = $(
        "#bb-media-details-description-container",
      );
      $descriptionContainer.removeClass("collapse");
      const $descriptionCollapseBtn = $("#description-collapse-button");
      $descriptionCollapseBtn.addClass("hidden");
    }
  }

  sync() {
    return this.mediaModel
      .fetch()
      .then(() => this.syncCollection())
      .then(() => {
        this.subPageView = this.addSubView(this.createSubPageView());
      });
  }

  syncCollection() {
    this.collection.state.currentPage = this.model.get("pageIndex");

    const mediaId = this.mediaModel.get("id");
    const contentSearchFilter = this.mediaModel.get("contentSearchFilter");
    const moduleQueryParams = {};

    // search filter form should be 'p=*&c=BOOK'
    if (contentSearchFilter) {
      console.log("ContentSearchFilter=%O", contentSearchFilter);
      _.forEach(_.split(contentSearchFilter, "&"), t => {
        const filter = _.split(t, "=");
        if (filter && filter.length === 2) {
          moduleQueryParams[filter[0]] = filter[1];
        }
      });
    } else {
      // if null/undefined legacy method
      console.log("ContentSearchFilter=%O", contentSearchFilter);
      moduleQueryParams["-pa"] = "*";
    }
    this.collection.queryParams = _.assign(
      {
        limit: PAGE_SIZE,
        offset:
          this.collection.state.currentPage * this.collection.state.pageSize,
        sort: true,
        "sort-fields": "name",
        _mod: mediaId,
      },
      moduleQueryParams,
    );

    return this.collection
      .fetch()
      .cancellable()
      .then(o => {
        return o;
      });
  }

  createNavView() {
    return new ModulePageNavView({
      model: this.model,
      moduleModel: this.mediaModel,
    });
  }

  getBreadcrumbText() {
    return this.mediaModel.get("displayName");
  }

  getDocumentTitle() {
    return `${this.mediaModel.get("displayName")}`;
  }

  createSubPageView() {
    let subPage;

    let pageViewOptions = {
      hasPreviousPage: this.collection.hasPreviousPage(),
      hasNextPage: this.collection.hasNextPage(),
      pageNumber: this.model.get("pageIndex") + 1,
      totalPages: Math.ceil(
        this.collection.totalRecords / this.collection.state.pageSize,
      ),
    };

    let presentationType = this.model.get("presentationType");

    if (presentationType === "list") {
      subPage = new PagerView(
        _.extend(
          {
            mediaView: new MediaListView({
              collection: this.collection,
            }),
          },
          pageViewOptions,
        ),
      );
    } else {
      subPage = new PagerView(
        _.extend(
          {
            mediaView: new MediaSalvattoreGridView({
              collection: this.collection,
            }),
          },
          pageViewOptions,
        ),
      );
    }

    subPage.on(
      "pageSequenceChanged",
      pageNumber => {
        this.model.set("pageIndex", pageNumber - 1);
        redirectToFragment(this.getUrlFragment());
      },
      this,
    );

    return subPage;
  }

  updateFragment() {
    const newFragment = this.getUrlFragment();
    replaceFragment(newFragment);
    chan("history").request("pushFragment", newFragment.replace(/^\//, ""));
    this.breadcrumbService.updateBreadcrumb();
  }

  getUrlFragment() {
    const pageIndex = this.model.get("pageIndex") || 0;
    const presentationType = this.model.get("presentationType");
    const moduleId = this.mediaModel.get("internalCode");
    return `/module/${moduleId}/${presentationType}/${pageIndex + 1}`;
  }

  onClickListView() {
    this.model.set("presentationType", "list");
    this.showSubPageView(Promise.resolve(this.createSubPageView()));
    this.updateFragment();
    return false;
  }

  onClickMasonryView() {
    this.model.set("presentationType", "masonry");
    this.showSubPageView(Promise.resolve(this.createSubPageView()));
    this.updateFragment();
    return false;
  }

  showSubPageView(subPagePromise) {
    if (this.showSubPagePromise && this.showSubPagePromise.isPending()) {
      this.showSubPagePromise.cancel();
    }

    chan("display").trigger("bodyViewLoading");

    this.showSubPagePromise = subPagePromise
      .then(view =>
        this.showSubView(
          "subPageView",
          view,
          $("#bb-media-details-sub-page-region"),
        ),
      )
      .catch(Promise.CancellationError, () => {
        // swallow cancellation
        console.debug("show sub page view cancelled");
      })
      .finally(value => {
        chan("display").trigger("bodyViewLoaded");

        if (this.navView.isStuck()) {
          $(window).scrollTop(this.navView.scrollTopToStick());
        }

        return value;
      });
  }
}

export default ModulePageView;
